import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';

type Status = 'loading' | 'success' | 'error';

export default function DiscordCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [discordUsername, setDiscordUsername] = useState<string | null>(null);
  const [discordAvatar, setDiscordAvatar] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      setErrorMessage(error === 'access_denied' ? 'You cancelled the Discord authorization.' : `Discord error: ${error}`);
      setStatus('error');
      return;
    }

    if (!code || !state) {
      setErrorMessage('Missing OAuth parameters.');
      setStatus('error');
      return;
    }

    api.post('/auth/discord/callback', { code, state })
      .then((res: any) => {
        setDiscordUsername(res.data?.discordUsername ?? null);
        setDiscordAvatar(res.data?.discordAvatar ?? null);
        setStatus('success');
      })
      .catch((err: any) => {
        setErrorMessage(err?.response?.data?.message || 'Failed to link Discord. Please try again.');
        setStatus('error');
      });
  }, [searchParams]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-2 border-gold border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-text-secondary">Linking your Discord account...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="card p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h1 className="font-western text-2xl text-cream mb-2">Link Failed</h1>
          <p className="text-text-secondary mb-6">{errorMessage}</p>
          <button onClick={() => navigate('/dashboard/settings')} className="btn-primary w-full">
            Back to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="card p-8 max-w-md w-full text-center">
        {/* Discord avatar */}
        {discordAvatar && (
          <img
            src={discordAvatar}
            alt="Discord avatar"
            className="w-20 h-20 rounded-full mx-auto mb-4 border-2 border-gold"
          />
        )}

        <div className="text-gold font-western text-3xl mb-2">Yeehaw, Partner!</div>
        <h1 className="text-cream text-xl font-semibold mb-1">Discord Linked</h1>
        {discordUsername && (
          <p className="text-text-secondary mb-6">
            Connected as <span className="text-gold font-semibold">{discordUsername}</span>
          </p>
        )}

        {/* Step-by-step instructions */}
        <div className="bg-background rounded-lg p-5 text-left mb-6 space-y-4">
          <p className="text-cream font-semibold text-sm uppercase tracking-wider mb-3">
            How to redeem your keys:
          </p>

          <div className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gold/20 text-gold flex items-center justify-center text-sm font-bold">1</span>
            <div>
              <p className="text-cream text-sm font-medium">Join the Outlaw Oasis Discord</p>
              <a
                href="https://discord.gg/u8Tuv7ns5r"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold hover:text-gold-bright text-sm underline"
              >
                discord.gg/u8Tuv7ns5r
              </a>
            </div>
          </div>

          <div className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gold/20 text-gold flex items-center justify-center text-sm font-bold">2</span>
            <div>
              <p className="text-cream text-sm font-medium">Go to your Purchases page</p>
              <p className="text-text-secondary text-sm">Find the redeem token for your key</p>
            </div>
          </div>

          <div className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gold/20 text-gold flex items-center justify-center text-sm font-bold">3</span>
            <div>
              <p className="text-cream text-sm font-medium">Run the redeem command in Discord</p>
              <code className="block mt-1 bg-leather text-gold px-3 py-2 rounded text-sm font-mono">
                /redeem &lt;your-token&gt;
              </code>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <a
            href="https://discord.gg/u8Tuv7ns5r"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary flex-1"
          >
            Join Discord
          </a>
          <button onClick={() => navigate('/dashboard/purchases')} className="btn-secondary flex-1">
            My Purchases
          </button>
        </div>
      </div>
    </div>
  );
}
